import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  FormHelperText,
  Switch,
  Box,
  Alert,
  CircularProgress,
} from '@mui/material';

function SiteForm({
  open,
  onClose,
  onSave,
  site,
  customers,
  customersLoading,
  saving,
  error,
}) {
  const [formData, setFormData] = useState({
    name: '',
    display_name: '',
    description: '',
    customer_id: '',
    base_url: '',
    api_key: '',
    group_key: '',
    enabled: true,
  });
  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    if (site) {
      setFormData({
        name: site.name || '',
        display_name: site.display_name || '',
        description: site.description || '',
        customer_id: site.customer_id || '',
        base_url: site.base_url || '',
        api_key: '', // Don't pre-fill secrets
        group_key: '', // Don't pre-fill secrets
        enabled: site.enabled !== false,
      });
    }
    setValidationErrors({});
  }, [site, open]);

  const handleChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear validation error when field changes
    if (validationErrors[field]) {
      setValidationErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const validate = () => {
    const errors = {};
    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    }
    if (!formData.base_url.trim()) {
      errors.base_url = 'Base URL is required';
    } else if (!formData.base_url.startsWith('http')) {
      errors.base_url = 'Base URL must start with http:// or https://';
    }
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;

    // Only include api_key and group_key if they were changed
    const updateData = {
      name: formData.name,
      display_name: formData.display_name || null,
      description: formData.description || null,
      customer_id: formData.customer_id || null,
      base_url: formData.base_url,
      enabled: formData.enabled,
    };

    if (formData.api_key.trim()) {
      updateData.api_key = formData.api_key;
    }
    if (formData.group_key.trim()) {
      updateData.group_key = formData.group_key;
    }

    onSave(site.id, updateData);
  };

  const handleClose = () => {
    setFormData({
      name: '',
      display_name: '',
      description: '',
      customer_id: '',
      base_url: '',
      api_key: '',
      group_key: '',
      enabled: true,
    });
    setValidationErrors({});
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Site</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 1 }}>
              {error}
            </Alert>
          )}

          <TextField
            label="Name"
            value={formData.name}
            onChange={handleChange('name')}
            required
            error={!!validationErrors.name}
            helperText={validationErrors.name || 'Internal identifier for the site'}
            fullWidth
          />

          <TextField
            label="Display Name"
            value={formData.display_name}
            onChange={handleChange('display_name')}
            helperText="Friendly name shown in the UI"
            fullWidth
          />

          <TextField
            label="Description"
            value={formData.description}
            onChange={handleChange('description')}
            multiline
            rows={2}
            fullWidth
          />

          <FormControl fullWidth>
            <InputLabel>Default Customer</InputLabel>
            <Select
              value={formData.customer_id}
              onChange={handleChange('customer_id')}
              label="Default Customer"
              disabled={customersLoading}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {customers.map((customer) => (
                <MenuItem key={customer.cust_id} value={customer.cust_id}>
                  {customer.cust_name}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              Default customer for newly synced cameras. Individual cameras can override this.
            </FormHelperText>
          </FormControl>

          <TextField
            label="Base URL"
            value={formData.base_url}
            onChange={handleChange('base_url')}
            required
            error={!!validationErrors.base_url}
            helperText={validationErrors.base_url || 'e.g., https://vms.example.com:8080'}
            fullWidth
          />

          {/* api_key/group_key fields removed — pending VMS migration */}

          <FormControlLabel
            control={
              <Switch
                checked={formData.enabled}
                onChange={handleChange('enabled')}
              />
            }
            label="Enabled"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} /> : null}
        >
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default SiteForm;
